import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { X, LogIn, Flame, BookOpen, AlertTriangle } from 'lucide-react';
import { CrescentStarIcon, RamadanLanternIcon, StarOrnament, KaabaIcon } from '../common/IslamicIcons';

const DISMISS_KEY   = 'signup_flyer_dismissed_until';
const DISMISS_HOURS = 48; // re-show after 48 h

const BENEFITS = [
  { icon: <Flame className="w-4 h-4" />, textKey: 'flyer.benefit1', color: 'text-amber-400' },
  { icon: <KaabaIcon size={16} primaryColor="currentColor" goldColor="currentColor" />, textKey: 'flyer.benefit2', color: 'text-violet-400' },
  { icon: <AlertTriangle className="w-4 h-4" />, textKey: 'flyer.benefit3', color: 'text-rose-400' },
  { icon: <BookOpen className="w-4 h-4" />, textKey: 'flyer.benefit4', color: 'text-emerald-400' },
];

const SignUpFlyer = ({ onLogin }) => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();
  const [visible, setVisible] = useState(false);   // controls whether mounted
  const [exiting, setExiting] = useState(false);   // controls exit animation

  // Show after 1.5s if not recently dismissed
  useEffect(() => {
    const dismissedUntil = localStorage.getItem(DISMISS_KEY);
    if (dismissedUntil && Date.now() < parseInt(dismissedUntil, 10)) return;
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_HOURS * 3600 * 1000));
    }, 380);
  };

  if (!visible) return null;

  const bgCard = isDarkMode
    ? 'bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f2744] border border-white/10'
    : 'bg-gradient-to-br from-[#0f172a] via-[#1a365d] to-[#0f2744] border border-white/10';

  return (
    <div
      data-testid="signup-flyer"
      className={`relative rounded-3xl overflow-hidden ${bgCard} ${
        exiting ? 'flyer-exit' : 'flyer-enter'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Shimmer scan */}
      <div className="shimmer-overlay" />

      {/* Floating Islamic decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-3 right-8 verse-float-b opacity-20">
          <RamadanLanternIcon size={30} color="#fbbf24" />
        </div>
        <div className="absolute bottom-4 left-6 verse-float-a opacity-15">
          <CrescentStarIcon size={24} color="#fbbf24" />
        </div>
        <div className="absolute top-1/2 right-4 verse-float-c opacity-10">
          <StarOrnament size={18} color="#fbbf24" />
        </div>
        <div className="absolute bottom-3 right-16 verse-float-b opacity-10" style={{ animationDelay: '3s' }}>
          <StarOrnament size={12} color="#fbbf24" />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 px-5 pt-5 pb-5">
        {/* Header row */}
        <div className={`flex items-start justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-2.5 icon-spin ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-10 h-10 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
              <CrescentStarIcon size={20} color="#fbbf24" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <p className="text-white font-bold text-sm leading-tight">
                {t('flyer.title')}
              </p>
              <p className="text-white/55 text-xs mt-0.5">
                {t('flyer.subtitle')}
              </p>
            </div>
          </div>
          <button
            data-testid="flyer-dismiss"
            onClick={dismiss}
            className="text-white/35 hover:text-white/70 transition-colors p-1 rounded-lg hover:bg-white/10 flex-shrink-0 ml-2"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Benefits list */}
        <div className="space-y-2.5 mb-5">
          {BENEFITS.map((b, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 benefit-slide-${i + 1} ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <div className={`w-7 h-7 rounded-xl bg-white/8 flex items-center justify-center flex-shrink-0 ${b.color}`}>
                {b.icon}
              </div>
              <span className="text-white/80 text-sm leading-tight">
                {t(b.textKey)}
              </span>
            </div>
          ))}
        </div>

        {/* CTA button */}
        <button
          data-testid="flyer-signin-btn"
          onClick={() => { dismiss(); onLogin?.(); }}
          className="cta-breathe w-full flex items-center justify-center gap-2.5 py-3 rounded-2xl bg-amber-500 hover:bg-amber-400 text-[#0f172a] font-bold text-sm transition-colors"
        >
          <LogIn className="w-4 h-4" />
          {t('flyer.cta')}
        </button>

        {/* Social proof */}
        <p className="text-center text-white/35 text-[11px] mt-3">
          {t('flyer.proof')}
        </p>
      </div>
    </div>
  );
};

export default SignUpFlyer;
